import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LiveSessionsService from '../../../Api/LiveSessionsService';
import CourseService from '../../../Api/CourseService';
import { toast } from 'react-toastify';
import Select from 'react-select';

const AddLiveSessions = () => {
    const navigate = useNavigate();
    const sessionsAPI = new LiveSessionsService();
    const courseAPI = new CourseService();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [courses, setCourses] = useState([]);
    const [formData, setFormData] = useState({
        course_id: '',
        title: '',
        description: '',
        session_datetime: '',
        session_duration_minutes: 0
    });

    // Fetch courses for dropdown
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await courseAPI.GetAllCourseList();
                if (response.data && response.data.data) {
                    setCourses(response.data.data);
                }
            } catch (err) {
                console.error('Error loading courses:', err);
                toast.error('Failed to load courses');
            }
        };

        fetchCourses();
    }, []);

    // Transform courses for react-select format
    const courseOptions = courses.map(course => ({
        value: course.id,
        label: course.title
    }));

    // Get current value for react-select
    const selectedCourseValue = courseOptions.find(option => 
        option.value === formData.course_id
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCourseChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            course_id: selectedOption ? selectedOption.value : ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            setError('');

            // Validate form data
            if (!formData.course_id) {
                throw new Error('Please select a course');
            }
            if (!formData.title) {
                throw new Error('Please enter a title');
            }
            if (!formData.description) {
                throw new Error('Please enter a description');
            }
            if (!formData.session_datetime) {
                throw new Error('Please select a session date and time');
            }
            if (!formData.session_duration_minutes || formData.session_duration_minutes <= 0) {
                throw new Error('Please enter a valid session duration');
            }

            // Create new session
            const response = await sessionsAPI.AddLiveSession(formData);

            if (response.data.status === "success") {
                toast.success('Session created successfully!');
                navigate('/live-sessions-list');
            } else {
                throw new Error(response.data.message || 'Failed to save session');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to save session');
            console.error('Session save error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <h4 className="text-xl text-primary-light mb-24">
                    Add New Live Session
                </h4>
                {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {error}
                    </div>
                )}
                {isLoading && !formData.title ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Course <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        name="course_id"
                                        options={courseOptions}
                                        value={selectedCourseValue}
                                        onChange={handleCourseChange}
                                        placeholder="Select a course..."
                                        isClearable
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Title <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter session title"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Enter session description"
                                        rows="4"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Session Date and Time <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        className="form-control"
                                        name="session_datetime"
                                        value={formData.session_datetime}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Duration (minutes) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="session_duration_minutes"
                                        value={formData.session_duration_minutes}
                                        onChange={handleInputChange}
                                        placeholder="Enter session duration in minutes"
                                        min="1"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-32">
                            <button
                                type="button"
                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Session'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddLiveSessions;
